import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useState, useEffect } from "react";
import {
  LayoutDashboard, FolderKanban, Library, LogOut, LineChart, LifeBuoy, Camera, Clock, ArrowUpRight, Sparkles, BookText, Sun, Moon, Map as MapIcon, Inbox, CalendarClock, Briefcase, Menu, X, Rocket, ClipboardList, Bell, Users, BookUser, GraduationCap, ScanSearch, FileSignature,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import api from "@/lib/api";

function TaskBell() {
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [flags, setFlags] = useState(0);
  useEffect(() => {
    let alive = true;
    const poll = () => api.get("/tasks/unseen").then((r) => { if (alive) { setCount(r.data.count); setFlags(r.data.flags || 0); } }).catch(() => {});
    poll();
    const t = setInterval(poll, 60000);
    return () => { alive = false; clearInterval(t); };
  }, []);
  const open = async () => {
    try { await api.post("/tasks/seen"); } catch { /* noop */ }
    setCount(0); setFlags(0);
    navigate("/dashboard/tasks");
  };
  return (
    <button onClick={open} data-testid="task-bell" aria-label={flags > 0 ? `${flags} raised issue${flags === 1 ? "" : "s"} need attention` : "Task alerts"}
      title={flags > 0 ? `${flags} raised issue${flags === 1 ? "" : "s"} from the team` : "Task alerts"}
      className={`relative h-9 w-9 flex items-center justify-center rounded-sm border transition-colors ${flags > 0 ? "border-red-400/60 text-red-300 hover:border-red-400 animate-pulse" : "border-gold-500/25 text-gold-400 hover:border-gold-500/60 hover:text-gold-300"}`}>
      <Bell className="h-4 w-4" />
      {count > 0 && (
        <span data-testid="task-bell-count"
          className={`absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 text-[0.6rem] font-bold rounded-full flex items-center justify-center ${flags > 0 ? "bg-red-500 text-white" : "bg-gold-500 text-navy-950"}`}>
          {count > 9 ? "9+" : count}
        </span>
      )}
    </button>
  );
}

const nav = [
  { to: "/dashboard", label: "Overview", ref: "S-00", icon: LayoutDashboard, end: true },
  { to: "/dashboard/portfolio", label: "Portfolio", ref: "S-01", icon: LineChart },
  { to: "/dashboard/projects", label: "Projects", ref: "S-02", icon: FolderKanban },
  { to: "/dashboard/raagent", label: "RAAgent", ref: "S-03", icon: LifeBuoy },
  { to: "/dashboard/fieldclaim", label: "FieldClaim", ref: "S-04", icon: Camera },
  { to: "/dashboard/drfa", label: "DRFA Programs", ref: "S-05", icon: CalendarClock },
  { to: "/dashboard/map", label: "Recovery Map", ref: "S-06", icon: MapIcon },
  { to: "/dashboard/timecapture", label: "TimeCapture", ref: "S-07", icon: Clock },
  { to: "/dashboard/ewprep", label: "EW Prep", ref: "S-18", icon: ScanSearch, admin: true },
  { to: "/dashboard/radar-review", label: "RADAR Review", ref: "S-19", icon: ScanSearch, admin: true },
  { to: "/dashboard/contracts", label: "Contract Admin", ref: "S-19", icon: FileSignature, admin: true },
  { to: "/dashboard/resources", label: "Resources", ref: "S-08", icon: Library },
  { divider: true },
  { to: "/dashboard/expert", label: "AI Expert", ref: "S-09", icon: Sparkles },
  { to: "/dashboard/library", label: "Document Library", ref: "S-10", icon: BookText },
  { to: "/dashboard/leads", label: "Leads", ref: "S-11", icon: Inbox, admin: true },
  { to: "/dashboard/business", label: "Business Hub", ref: "S-12", icon: Briefcase, admin: true },
  { to: "/dashboard/alpha", label: "Alpha HQ", ref: "S-13", icon: Rocket, admin: true },
  { to: "/dashboard/tasks", label: "Task Board", ref: "S-14", icon: ClipboardList, admin: true },
  { to: "/dashboard/contacts", label: "Contacts", ref: "S-16", icon: BookUser, admin: true },
  { to: "/dashboard/induction", label: "Induction", ref: "S-17", icon: GraduationCap, admin: true },
  { to: "/dashboard/team", label: "Team", ref: "S-15", icon: Users, adminStrict: true },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const initials = (user?.name || "U").split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();

  const [theme, setTheme] = useState(() => localStorage.getItem("mdau_dash_theme") || "dark");
  useEffect(() => { localStorage.setItem("mdau_dash_theme", theme); }, [theme]);

  const [menuOpen, setMenuOpen] = useState(false);
  const visibleNav = nav.filter((n) => (!n.admin || ["admin", "staff"].includes(user?.role)) && (!n.adminStrict || user?.role === "admin"));

  return (
    <div className={`min-h-screen bg-navy-950 flex ${theme === "light" ? "theme-light" : ""}`}>
      {/* Sidebar — sheet index */}
      <aside className="w-64 bg-navy-950 text-white flex-col hidden md:flex fixed h-screen border-r border-gold-500/20" data-testid="dashboard-sidebar">
        <div className="px-6 h-20 flex items-center border-b border-gold-500/20">
          <Logo variant="light" />
        </div>
        <div className="px-6 py-4 border-b border-gold-500/15">
          <span className="font-mono uppercase tracking-[0.2em] text-[0.5rem] text-gold-500/60">Sheet Index</span>
        </div>
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto min-h-0">
          {visibleNav.map((n) => {
            if (n.divider) {
              return <div key="nav-divider" className="my-3 mx-4 border-t border-gold-500/15" data-testid="nav-divider" />;
            }
            const testid = `sidebar-${n.label.toLowerCase().replace(/\s/g, "-")}`;
            const base = "group flex items-center gap-3 px-4 py-3 rounded-sm text-sm transition-colors";
            const inner = (isActive) => (
              <>
                <span className={`font-mono text-[0.6rem] tracking-widest ${isActive ? "text-navy-900/70" : "text-gold-500/60"}`}>{n.ref}</span>
                <n.icon className="h-4 w-4" />
                <span className="flex-1">{n.label}</span>
                {n.external && <ArrowUpRight className="h-3.5 w-3.5 text-gold-500/50" />}
                {n.soon && <span className="font-mono text-[0.45rem] uppercase tracking-widest text-white/35 border border-white/15 px-1 py-0.5 rounded-sm">Soon</span>}
              </>
            );
            if (n.soon) {
              return <div key={n.label} data-testid={testid} className={`${base} text-white/40 cursor-default`}>{inner(false)}</div>;
            }
            if (n.external) {
              return <a key={n.label} href={n.href} target="_blank" rel="noreferrer" data-testid={testid} className={`${base} text-white/65 hover:bg-navy-800 hover:text-white`}>{inner(false)}</a>;
            }
            return (
              <NavLink key={n.to} to={n.to} end={n.end} data-testid={testid}
                className={({ isActive }) => `${base} ${isActive ? "bg-gold-500 text-navy-950 font-semibold" : "text-white/65 hover:bg-navy-800 hover:text-white"}`}>
                {({ isActive }) => inner(isActive)}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-4 border-t border-gold-500/20">
          <button
            onClick={() => { logout(); navigate("/"); }}
            data-testid="logout-button"
            className="flex items-center gap-3 px-4 py-3 w-full text-sm text-white/65 hover:text-gold-400 transition-colors"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 md:ml-64 flex flex-col min-h-screen">
        {/* title-block header */}
        <header className="bg-navy-950 border-b border-gold-500/20 h-20 flex items-stretch sticky top-0 z-30">
          <div className="flex-1 grid grid-cols-2 md:grid-cols-4">
            {[
              ["Operator", user?.name || "—"],
              ["Company", user?.company || "—"],
              ["Role", user?.title || user?.role || "—"],
              ["Status", "Active"],
            ].map(([k, v], i) => (
              <div key={i} className="px-5 py-3 border-r border-gold-500/15 flex flex-col justify-center hidden md:flex">
                <span className="font-mono uppercase tracking-[0.2em] text-[0.5rem] text-gold-500/60">{k}</span>
                <span className="font-mono text-sm text-white mt-0.5 truncate">{v}</span>
              </div>
            ))}
          </div>
          <div className="px-5 flex items-center gap-3 md:hidden">
            <button
              onClick={() => setMenuOpen(true)}
              data-testid="mobile-menu-btn"
              aria-label="Open menu"
              className="h-10 w-10 flex items-center justify-center rounded-sm border border-gold-500/30 text-gold-400"
            >
              <Menu className="h-5 w-5" />
            </button>
            <span className="font-heading text-white truncate max-w-[130px]">{user?.name}</span>
          </div>
          <div className="px-5 flex items-center gap-3">
            <TaskBell />
            <button
              onClick={() => setTheme((t) => (t === "light" ? "dark" : "light"))}
              data-testid="theme-toggle"
              aria-label="Toggle background"
              className="h-9 w-9 flex items-center justify-center rounded-sm border border-gold-500/25 text-gold-400 hover:border-gold-500/60 hover:text-gold-300 transition-colors"
            >
              {theme === "light" ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
            </button>
            <div className="h-11 w-11 bg-gold-500 text-navy-950 flex items-center justify-center font-heading font-bold rounded-sm" data-testid="user-avatar">
              {initials}
            </div>
          </div>
        </header>
        <main className="flex-1 p-6 lg:p-10 bp-grid">
          <Outlet />
        </main>
      </div>

      {/* Mobile nav drawer */}
      {menuOpen && (
        <div className="fixed inset-0 z-50 md:hidden" data-testid="mobile-nav-drawer">
          <div className="absolute inset-0 bg-navy-950/80" onClick={() => setMenuOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-72 max-w-[85vw] bg-navy-950 border-r border-gold-500/25 flex flex-col">
            <div className="px-5 h-20 flex items-center justify-between border-b border-gold-500/20">
              <Logo variant="light" />
              <button onClick={() => setMenuOpen(false)} data-testid="mobile-menu-close" aria-label="Close menu" className="text-white/60 p-2">
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
              {visibleNav.map((n) =>
                n.divider ? (
                  <div key="m-divider" className="my-3 mx-4 border-t border-gold-500/15" />
                ) : (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.end}
                    onClick={() => setMenuOpen(false)}
                    data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-4 py-3.5 rounded-sm text-sm transition-colors ${isActive ? "bg-gold-500 text-navy-950 font-semibold" : "text-white/70"}`
                    }
                  >
                    <span className="font-mono text-[0.6rem] tracking-widest opacity-70">{n.ref}</span>
                    <n.icon className="h-4 w-4" />
                    {n.label}
                  </NavLink>
                )
              )}
            </nav>
            <div className="p-4 border-t border-gold-500/20">
              <button
                onClick={() => { logout(); navigate("/"); }}
                data-testid="mobile-logout-button"
                className="flex items-center gap-3 px-4 py-3 w-full text-sm text-white/65"
              >
                <LogOut className="h-4 w-4" /> Sign out
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
